import { Anchor, Button, CollapseLayer, CollapseLayerGrid, CollapseLayerProvider, CollapseTrigger, Grid, GridColumn, Spinner } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import AnalyticsService from '../../../services/AnalyticsService';
import { RouteService } from '../../../services/RouteService';
import { IInvoicesStore } from '../../../stores/InvoicesStore';
import { IPreviousInvoicesFilterStore } from '../../../stores/PreviousInvoicesFilterStore';
import { ISsoStore } from '../../../stores/SsoStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import BoxNotice from '../../common/BoxNotice';
import PreviousInvoicesFailedToLoadNotice from '../notices/PreviousInvoicesFailedToLoadNotice';

import { formatDate, parseToISODate } from '../../../util/DateUtil';
import PreviousInvoicesFilter from './PreviousInvoicesFilter';
import PreviousInvoicesTable from './PreviousInvoicesTable';

interface IPreviousInvoicesProps {
  invoicesStore?: IInvoicesStore;
  ssoStore?: ISsoStore;
  translateStore?: ITranslateStore;
  routeService?: RouteService;
  previousInvoicesFilterStore?: IPreviousInvoicesFilterStore;
  analyticsService?: AnalyticsService;
}

@inject('invoicesStore', 'routeService', 'ssoStore', 'translateStore', 'previousInvoicesFilterStore', 'analyticsService')
@observer
export default class PreviousInvoices extends React.Component<IPreviousInvoicesProps> {
  public componentDidMount(): void {
    if (this.props.invoicesStore.previousInvoices.isInitial) {
      this.props.invoicesStore.loadPreviousInvoices(this.props.previousInvoicesFilterStore.invoiceFilter);
    }
  }

  public render(): JSX.Element {
    const translate = this.props.translateStore.translate;

    return (
      <React.Fragment>
        <CollapseLayerGrid
          id="collapse-1"
          className="text__margin-double"
          align={['between-xs']}
          onActiveCollapseChange={(activeCollapse) => {
            if (activeCollapse == null) {
              this.props.previousInvoicesFilterStore.reset();
            }
          }}
        >
          <GridColumn width={['xs']}>
            <h2 className="h3">{translate('invoices.previous-invoices.title')}</h2>
          </GridColumn>
          <GridColumn width={['xs-12', 'md-6']}>
            <Grid align={['between-xs', 'end-md']}>
              <GridColumn width={['xs']}>
                <Button
                  text={translate('payment-history.title')}
                  id={'previous-invoices-payment-history-button'}
                  iconRight="#arrow-right"
                  size="small"
                  onClick={() => {
                    this.props.routeService.navigateToPaymentHistoryPage();
                    this.props.analyticsService.previousInvoicesViewPaymentHistory();
                  }}
                />
              </GridColumn>
              <GridColumn width={['xs']}>
                <CollapseLayerProvider id="collapse-filters">
                  <CollapseTrigger>
                    <Button variant="secondary" text={translate('invoices.previous-invoices.filter.button')} size="small" />
                  </CollapseTrigger>
                  <CollapseLayer>
                    <PreviousInvoicesFilter />
                  </CollapseLayer>
                </CollapseLayerProvider>
              </GridColumn>
            </Grid>
          </GridColumn>
        </CollapseLayerGrid>
        {this.previousInvoicesTable()}
      </React.Fragment>
    );
  }

  private previousInvoicesTable() {
    const translate = this.props.translateStore.translate;
    const previousInvoices = this.props.invoicesStore.previousInvoices;

    if (previousInvoices.hasFailed) {
      return <PreviousInvoicesFailedToLoadNotice />;
    }

    if (previousInvoices.isInitialLoading) {
      return <Spinner />;
    }

    const previousInvoicesFilter = this.props.invoicesStore.previousInvoicesFilter;

    if (previousInvoices.isLoadedAndEmpty) {
      return (
        <BoxNotice>
          {translate('invoices.previous-invoices.notices.no-previous-invoices', {
            fromDate: formatDate(parseToISODate(previousInvoicesFilter.fromDate)),
            toDate: formatDate(parseToISODate(previousInvoicesFilter.toDate))
          } as any)}
        </BoxNotice>
      );
    }

    return (
      <React.Fragment>
        <PreviousInvoicesTable />
        <div className="text-center">{this.viewMorePreviousInvoices()}</div>
      </React.Fragment>
    );
  }

  private viewMorePreviousInvoices() {
    const translate = this.props.translateStore.translate;
    const invoicesStore = this.props.invoicesStore;
    const previousInvoices = this.props.invoicesStore.previousInvoices;
    const analyticsService = this.props.analyticsService;

    if (previousInvoices.isFullyLoaded) {
      return null;
    }

    if (previousInvoices.isLoading) {
      return <Spinner />;
    }

    return (
      <Anchor
        url="#"
        onClick={() => {
          invoicesStore.loadMorePreviousInvoices();
          analyticsService.previousInvoicesViewMore();
        }}
      >
        {translate('invoices.previous-invoices.view-more')}
      </Anchor>
    );
  }
}
